import { useGetAuthoringComponentQuery, useGetAuthoringComponentRevisionQuery } from './authoring'

/**
 * The component at the other end of an edge, at the revision that edge
 * resolves to.
 *
 * **A pinned edge reads its own revision**, not the head: a placement
 * fixed to revision 3 composes to revision 3 forever, so a screen that
 * named it from the latest would report a heading the visitor is not
 * being shown. That is also why the pinned branch is cached forever —
 * a past revision is immutable, which is the whole reason a pin is
 * `(id, revision)`.
 *
 * Two queries and one of them always skipped, because hooks cannot be
 * conditional. The caller sees one result either way.
 *
 * **It is a read per target, and that is the deliberate part.** A
 * document carries a linked child's id and nothing else — the child's
 * content lives in its own stream and was never fetched — so the only
 * way to say what a placement points at is to ask. The reads are point
 * reads keyed by `(tenant, id)`, so a component linked from four places
 * is fetched once and opening it afterwards costs nothing. It is the
 * same trade a thumbnail grid and the repeated-content preview already
 * make, and it is right for the same reason: an admin tool browsing its
 * own tenant is where a read is cheapest and a uuid on screen is
 * dearest.
 */
export function useTargetDocument(tenant: string, id?: string, revision?: number) {
  const pinned = revision !== undefined
  const head = useGetAuthoringComponentQuery({ tenant, id: id ?? '' }, { skip: !id || pinned })
  const at = useGetAuthoringComponentRevisionQuery(
    { tenant, id: id ?? '', revision: revision ?? 1 },
    { skip: !id || !pinned },
  )
  return pinned ? at : head
}
